import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Two independent shoulder-surf protections, both session-only (not
 * persisted). Backend is the source of truth for what a CLIENT user can
 * actually see; these toggles only mask data already in the clinician's
 * browser.
 *
 * - ``piiMode``: hides identifying fields (name → initials, photo →
 *   silhouette, email/phone/address → •••, government IDs → ••••,
 *   DOB → ••/••/••••). Note bodies stay visible.
 * - ``privacyMode``: hides clinical note bodies behind a click-to-reveal
 *   placeholder. Identity fields are unaffected.
 *
 * Both can be on at once (= full mask). Toggle state resets on page
 * refresh by design — the slice is left out of the redux-persist
 * whitelist in ``store.ts``.
 */
interface PrivacyState {
  piiMode: boolean;
  privacyMode: boolean;
}

const initialState: PrivacyState = {
  piiMode: false,
  privacyMode: false,
};

export const privacySlice = createSlice({
  name: 'privacy',
  initialState,
  reducers: {
    togglePiiMode: (state) => {
      state.piiMode = !state.piiMode;
    },
    togglePrivacyMode: (state) => {
      state.privacyMode = !state.privacyMode;
    },
    setPiiMode: (state, action: PayloadAction<boolean>) => {
      state.piiMode = action.payload;
    },
    setPrivacyMode: (state, action: PayloadAction<boolean>) => {
      state.privacyMode = action.payload;
    },
    resetPrivacy: () => initialState,
  },
});

export const {
  togglePiiMode,
  togglePrivacyMode,
  setPiiMode,
  setPrivacyMode,
  resetPrivacy,
} = privacySlice.actions;

export default privacySlice.reducer;
